import React, { useEffect, memo, useCallback, useState } from "react";
import { FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { FieldOwnershipBadge } from "@/components/common/FieldOwnershipBadge";
import { FieldVendorHint } from "@/components/common/FieldVendorHint";
import { UseFormReturn } from "react-hook-form";
import { OfferFormValues } from "../../../schemas/offerFormSchema";
import { CampLocation } from "@/types/campLocation";
import { Variation } from "@/components/admin/locations/types/variation";
import { useIncrementOptions } from "@/components/admin/locations/hooks/useIncrementOptions";
import { getDecimalEnums } from "@/services/decimalEnumService";
import { db } from "@/services/firebaseService";

interface BasicInformationSectionProps {
  form: UseFormReturn<OfferFormValues>;
  location: CampLocation;
  markFieldAsModified?: (fieldName: string) => void;
  isAdmin?: boolean;
}

/**
 * BasicInformationSection Component
 * 
 * Renders the program selection and session title fields in the offer/session modal.
 * This component handles the core identification fields for creating or editing sessions.
 * 
 * @param form - React Hook Form instance for the offer form
 * @param location - Camp location data containing available program variations
 * @param markFieldAsModified - Optional callback to track field modifications
 * @param isAdmin - Boolean indicating if the current user is an admin
 */
export const BasicInformationSection: React.FC<BasicInformationSectionProps> = memo(({
  form,
  location,
  markFieldAsModified = () => {},
  isAdmin = false
}) => {
  // JAIL BREAK: Local state for instant UI updates
  const [selectedProgramLocal, setSelectedProgramLocal] = useState<string>("");
  const [showSessionLengthLocal, setShowSessionLengthLocal] = useState<boolean>(false);
  const [sessionLengthOptionsLocal, setSessionLengthOptionsLocal] = useState<any[]>([]);
  const [defaultIncrementLocal, setDefaultIncrementLocal] = useState<string>("");
  const [enumsMap, setEnumsMap] = useState<Map<string, any>>(new Map());
  
  // Load increment options hook
  const { incrementOptions, loading: incrementOptionsLoading } = useIncrementOptions();
  
  // Safe access to programs (variations) with fallback to empty array
  const programs: Variation[] = location?.variations || [];
  const selectedProgramKey = form.watch("variationKey");
  const currentProgram = form.watch("variation");
  const numDaysOverride = form.watch("numDaysOverride");

  // Load decimal enums for increment calculations
  useEffect(() => {
    const loadEnums = async () => {
      try {
        const enums = await getDecimalEnums(db, "offerIncrement");
        const enumMap = new Map();
        enums.forEach(enumItem => {
          enumMap.set(enumItem.id, enumItem);
        });
        setEnumsMap(enumMap);
      } catch (error) {
        console.error("[BasicInformationSection] Error loading decimal enums:", error);
      }
    };
    loadEnums();
  }, []);

  // Function to calculate increment options for a program
  const calculateIncrementOptions = useCallback((program: Variation) => {
    if (!program || enumsMap.size === 0) return [];
    
    
    const options = [];
    
    // Add the default/regular increment option
    if (program.regularOfferIncrementKey) {
      const enumItem = enumsMap.get(program.regularOfferIncrementKey);
      if (enumItem) {
        options.push({
          value: program.regularOfferIncrementKey,
          label: enumItem.longLabel || enumItem.shortLabel || program.regularOfferIncrementKey,
          isDefault: true
        });
      }
    }
    
    // Add alternative increment options
    if (program.regularOfferIncrementAlternatives && program.regularOfferIncrementAlternatives.length > 0) {
      for (const alternative of program.regularOfferIncrementAlternatives) {
        const enumItem = enumsMap.get(alternative.key);
        if (enumItem) {
          options.push({
            value: alternative.key,
            label: enumItem.longLabel || enumItem.shortLabel || alternative.key,
            isDefault: false
          });
        }
      }
    }
    
    return options;
  }, [enumsMap]);

  // JAIL BREAK: Calculate and update session length options instantly when program changes
  const updateSessionLengthInstantly = useCallback((programKey: string) => {
    if (!programKey || programs.length === 0) {
      setShowSessionLengthLocal(false);
      setSessionLengthOptionsLocal([]);
      setDefaultIncrementLocal("");
      return;
    }
    
    const selectedProgram = programs.find(program => program.key === programKey);
    if (!selectedProgram) {
      setShowSessionLengthLocal(false);
      setSessionLengthOptionsLocal([]);
      setDefaultIncrementLocal("");
      return;
    }
    
    
    const incrementOptions = calculateIncrementOptions(selectedProgram);
    const totalOptions = incrementOptions.length;
    
    
    if (totalOptions > 1) {
      // Show session length selector when there are multiple options
      setShowSessionLengthLocal(true);
      setSessionLengthOptionsLocal(incrementOptions);
      
      // CRITICAL FIX: Don't auto-set default! Let detection logic handle it properly
      // setDefaultIncrementLocal(selectedProgram.regularOfferIncrementKey || "");
      setDefaultIncrementLocal(""); // Will be set by detection logic
      
    } else {
      // Hide session length selector when there's only 1 option (just the default)
      setShowSessionLengthLocal(false);
      setSessionLengthOptionsLocal([]);
      setDefaultIncrementLocal("");
      
    }
  }, [programs, calculateIncrementOptions]);

  // NEW: Smart session length detection for existing sessions
  const detectAndSetSessionLength = useCallback((program: Variation) => {
    if (!program || enumsMap.size === 0) return;
    
    const incrementOptions = calculateIncrementOptions(program);
    if (incrementOptions.length <= 1) return; // No need to detect if there's only 1 option
    
    
    // Get current form values that might help us detect the correct session length
    const myOfferIncrementKey = form.getValues("myOfferIncrementKey");
    const numDaysOverride = form.getValues("numDaysOverride");
    
    let detectedIncrementKey = "";
    
    // Test 1: Use myOfferIncrementKey if it's valid and matches one of the program's options
    if (myOfferIncrementKey) {
      const matchingOption = incrementOptions.find(option => option.value === myOfferIncrementKey);
      if (matchingOption) {
        detectedIncrementKey = myOfferIncrementKey;
        // Don't modify numDaysOverride - it should already be correct
        // form.setValue("numDaysOverride", enumsMap.get(myOfferIncrementKey)?.value || null);
      }
    }
    
    // Test 2: Use numDaysOverride to find matching session length
    if (!detectedIncrementKey && numDaysOverride && numDaysOverride > 0) {
      
      // Find increment option that matches the current numDaysOverride
      for (const option of incrementOptions) {
        const enumItem = enumsMap.get(option.value);
        if (enumItem && enumItem.value === numDaysOverride) {
          detectedIncrementKey = option.value;
          // Store the detected increment key for future edits (without triggering change handlers)
          form.setValue("myOfferIncrementKey", option.value);
          break;
        }
      }
    }
    
    // Test 3: Fallback to program's default session length
    if (!detectedIncrementKey && program.regularOfferIncrementKey) {
      detectedIncrementKey = program.regularOfferIncrementKey;
      
      // Only set the form values if they're not already set correctly
      const currentNumDaysOverride = form.getValues("numDaysOverride");
      const enumItem = enumsMap.get(program.regularOfferIncrementKey);
      if (enumItem && enumItem.value && (!currentNumDaysOverride || currentNumDaysOverride !== enumItem.value)) {
        form.setValue("numDaysOverride", enumItem.value);
        form.setValue("myOfferIncrementKey", program.regularOfferIncrementKey);
      }
    }
    
    // CRITICAL: Update local UI state with detected value to show correct selection
    if (detectedIncrementKey) {
      setDefaultIncrementLocal(detectedIncrementKey);
    }
    
    return detectedIncrementKey;
  }, [programs, calculateIncrementOptions, enumsMap, form]);

  // JAIL BREAK: Initialize local state from form on mount
  useEffect(() => {
    const initialValue = form.getValues("variationKey");
    if (initialValue && selectedProgramLocal === "") {
      setSelectedProgramLocal(initialValue);
      updateSessionLengthInstantly(initialValue);
    }
  }, [updateSessionLengthInstantly]); // Only on dependencies
  
  // NEW: Smart session length detection for existing sessions - runs after enums are loaded
  useEffect(() => {
    const initialValue = form.getValues("variationKey");
    
    if (initialValue && enumsMap.size > 0 && programs.length > 0) {
      const selectedProgram = programs.find(program => program.key === initialValue);
      if (selectedProgram) {
        detectAndSetSessionLength(selectedProgram);
        // CRITICAL: Re-update the session length display after detection
        updateSessionLengthInstantly(initialValue);
      }
    }
  }, [enumsMap.size, programs, detectAndSetSessionLength, updateSessionLengthInstantly]); // Run when enums are loaded

  // JAIL BREAK: Sync local state with form when it changes
  useEffect(() => {
    if (selectedProgramKey !== selectedProgramLocal) {
      setSelectedProgramLocal(selectedProgramKey || "");
      updateSessionLengthInstantly(selectedProgramKey || "");
    }
  }, [selectedProgramKey, selectedProgramLocal, updateSessionLengthInstantly]);

  // Memoized handlers for better performance
  const handleProgramChange = useCallback((value: string) => {
    try {
      
      // JAIL BREAK: Update local state IMMEDIATELY for instant UI update
      setSelectedProgramLocal(value);
      updateSessionLengthInstantly(value);
      
      markFieldAsModified("variationKey");
      
      // CRITICAL FIX: Dispatch the event immediately when program is selected
      // Don't wait for useEffect which might not run due to form reinitialization
      if (value && programs.length > 0) {
        const selectedProgram = programs.find(program => program.key === value);
        if (selectedProgram) {
          
          // Log program details immediately
          
          // Set the variation in the form immediately
          form.setValue("variation", selectedProgram);
          
          // NEW: Auto-populate session title for admin users
          if (isAdmin && selectedProgram.displayTitle) {
            form.setValue("displayTitle", selectedProgram.displayTitle);
            markFieldAsModified("displayTitle");
          }
          
          // Dispatch the event immediately
          const variationChangedEvent = new CustomEvent('variationChanged', {
            detail: {
              variationData: selectedProgram,
              variationKey: value
            }
          });
          
          
          window.dispatchEvent(variationChangedEvent);
        }
      }
    } catch (error) {
      console.error("[BasicInformationSection] Error in handleProgramChange:", error);
    }
  }, [markFieldAsModified, form, programs, isAdmin, updateSessionLengthInstantly, calculateIncrementOptions, enumsMap]);

  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      markFieldAsModified("displayTitle");
    } catch (error) {
      console.error("[BasicInformationSection] Error in handleTitleChange:", error);
    }
  }, [markFieldAsModified]);

  // JAIL BREAK: Handle session length change
  const handleSessionLengthChange = useCallback((value: string) => {
    try {
      
      // Calculate numDays from the selected increment key
      const enumItem = enumsMap.get(value);
      if (enumItem && enumItem.value) {
        form.setValue("numDaysOverride", enumItem.value);
        markFieldAsModified("numDaysOverride");
        
        // NEW: Store the selected increment key for future edits
        form.setValue("myOfferIncrementKey", value);
        markFieldAsModified("myOfferIncrementKey");
        
        // 🔍 DEBUGGING: Verify the form values were set correctly
      }
    } catch (error) {
      console.error("[BasicInformationSection] Error in handleSessionLengthChange:", error);
    }
  }, [form, markFieldAsModified, enumsMap]);

  // Update program data when program key changes and dispatch event for other sections
  useEffect(() => {
    
    if (selectedProgramKey && programs.length > 0) {
      try {
        const selectedProgram = programs.find(program => program.key === selectedProgramKey);
        
        if (selectedProgram && selectedProgram !== currentProgram) {
          
          // ENHANCED DEBUG: Log the specific fields that dependent sections need
          
          // Set the variation in the form
          form.setValue("variation", selectedProgram);
          
          // CRITICAL FIX: Dispatch custom event to notify other sections of the variation change
          // This allows PricingSection and TimingSection to update their fields with program values
          const variationChangedEvent = new CustomEvent('variationChanged', {
            detail: {
              variationData: selectedProgram,
              variationKey: selectedProgramKey
            }
          });
          
          
          window.dispatchEvent(variationChangedEvent);
        }
      } catch (error) {
        console.error("[BasicInformationSection] Error updating program:", error);
      }
    }
  }, [selectedProgramKey, programs, form, currentProgram]);

  // Show message when location data is not available
  if (!location) {
    return (
      <div className="space-y-4">
        <div className="text-sm text-muted-foreground">
          Location data is not available.
        </div>
      </div>
    );
  }

  // Show message when no programs are available
  if (programs.length === 0) {
    return (
      <div className="space-y-4">
        <div className="text-sm text-muted-foreground">
          No programs available. Please add programs to this location first.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="variationKey"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Program <span className="text-red-500">*</span></FormLabel>
            <Select
              value={field.value}
              onValueChange={(value) => {
                field.onChange(value);
                handleProgramChange(value);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select program" />
              </SelectTrigger>
              <SelectContent>
                {programs.map((program) => (
                  <SelectItem key={program.key} value={program.key}>
                    {program.displayTitle || program.key}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* JAIL BREAK: Session Length Selector - shows instantly when program has multiple increment options */}
      {showSessionLengthLocal && (
        <FormField
          control={form.control}
          name="numDaysOverride"
          render={({ field }) => {
            // Find the current increment key based on numDaysOverride value
            const currentNumDays = field.value;
            let currentIncrementKey = "";
            
            if (currentNumDays && enumsMap.size > 0) {
              // Find the increment key that matches the current numDays
              for (const option of sessionLengthOptionsLocal) {
                const enumItem = enumsMap.get(option.value);
                if (enumItem && enumItem.value === currentNumDays) {
                  currentIncrementKey = option.value;
                  break;
                }
              }
            }
            
            // If no match found, use the default increment key
            if (!currentIncrementKey && defaultIncrementLocal) {
              currentIncrementKey = defaultIncrementLocal;
            }
            
            return (
              <FormItem>
                <FormLabel>Session length <span className="text-red-500">*</span></FormLabel>
                <Select
                  value={currentIncrementKey}
                  onValueChange={(value) => {
                    handleSessionLengthChange(value);
                  }}
                  disabled={incrementOptionsLoading || sessionLengthOptionsLocal.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select session length" />
                  </SelectTrigger>
                  <SelectContent>
                    {sessionLengthOptionsLocal.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                        {option.isDefault && " (default)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      )}
      
      <FormField
        control={form.control}
        name="displayTitle"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center gap-2">
              <FormLabel>Session title <span className="text-red-500">*</span></FormLabel>
              <FieldOwnershipBadge fieldPath="/displayTitle" />
            </div>
            <Input
              {...field}
              placeholder="Example: Summer Week 1"
              maxLength={100}
              onChange={(e) => {
                field.onChange(e.target.value);
                handleTitleChange(e);
              }}
            />
            <FieldVendorHint fieldPath="/displayTitle" onUseVendorValue={(v) => form.setValue("displayTitle", v as string)} />
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
});

// Add display name for debugging
BasicInformationSection.displayName = "BasicInformationSection";
